import { env, hasSheets } from "./env";

export type WaitlistSource = "hero" | "footer" | "portal" | "followup";

export interface SheetsFields {
  email: string;
  source: WaitlistSource;
  domains?: string;
  domainOther?: string;
  role?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  referrer?: string;
  painPoint?: string;
  honeypot?: string;
}

export async function submitToSheets(fields: SheetsFields): Promise<void> {
  // Honeypot — silently succeed without sending. Bots shouldn't get a hint
  // they were caught (no error response, no retry signal).
  if (fields.honeypot && fields.honeypot.trim().length > 0) {
    return;
  }

  if (!hasSheets()) {
    if (import.meta.env.DEV) {
      console.info("[sheets] VITE_SHEETS_WEBHOOK_URL not set — skipping submit", fields);
      return;
    }
    throw new Error("Waitlist is not configured. Please try again later.");
  }

  const payload = {
    ...fields,
    submittedAt: new Date().toISOString(),
  };

  // text/plain;charset=utf-8 is a CORS "simple request" content-type, so the
  // browser skips the OPTIONS preflight that Apps Script Web Apps don't handle.
  const res = await fetch(env.sheetsWebhookUrl, {
    method: "POST",
    headers: { "Content-Type": "text/plain;charset=utf-8" },
    body: JSON.stringify(payload),
    redirect: "follow",
  });

  if (!res.ok) {
    throw new Error(`Submission failed (${res.status}). Please try again.`);
  }

  // Apps Script returns JSON via ContentService. Best-effort parse — if the
  // server reports {status: "error"}, surface it. Network success but server
  // rejection still counts as a failed submission.
  try {
    const body = (await res.json()) as { status?: string; reason?: string };
    if (body.status && body.status !== "ok") {
      throw new Error(body.reason ? `Submission rejected: ${body.reason}` : "Submission rejected");
    }
  } catch (err) {
    if (err instanceof Error && err.message.startsWith("Submission")) throw err;
    // Non-JSON response — treat 2xx as success.
  }
}
